import { useTranslations } from "next-intl";
import { CoveringLoadingSpinner } from "../../LoadingSpinner/CoveringLoadingSpinner";
import { CoweringDropDown } from "../../DropDown/CoweringDropDown";
import { PDeleteConfirmation } from "../../PDeleteConfirmation/PDeleteConfirmation";
import { PDuplicateEvent } from "../../PDuplicateEvent/PDuplicateEvent";
import { PShowToast } from "../../PShowToast/PShowToast";
import { useCTA } from "./hooks/useCTA";
import { useMetaTable } from "@/providers/MetaTableProvider";
import { AnimatePresence } from "framer-motion";
import { Plugin } from "@/types/enums/Plugin";
import { useMemo } from "react";
import { EventRowDTO } from "@/types/EventRowDTO";
import { IconCheck } from "@tabler/icons-react";
import { resolveEventCaps } from "@/lib/authz/eventLanding";
import { Capability } from "@/types/Capability";
import { RegistrationType } from "@/types/enums/RegistrationType";
import { EventDeletePaidPlanNotice } from "../../EventDeletePaidPlanNotice/EventDeletePaidPlanNotice";
import { hasPaidEventPlan } from "@/lib/utils/event-plan-catalog";

type TableCellCTAProps = {
  isEditing: boolean;
  event: EventRowDTO;
};

export const TableCellContentCTA: React.FC<TableCellCTAProps> = ({
  isEditing,
  event,
}) => {
  const t = useTranslations("cta-event");
  const t2 = useTranslations("event-delete-popup");
  const t3 = useTranslations("duplicateEvent");
  const tBulk = useTranslations("bulkConfirm");
  const { archived } = useMetaTable();
  const {
    router,
    isLoading,
    isDeleting,
    actionError,
    showDeleteConfirmation,
    handleCancelDelete,
    handleDeleteConfirmed,
    handleDropdownAction,
    handleNestedAction,
    showDuplicateModal,
    isDuplicating,
    duplicateError,
    showDuplicateToast,
    handleDuplicateConfirmed,
    handleCancelDuplicate,
  } = useCTA({ eventId: event.eventId! });

  // Dynamically generate dropdown items based on event plugins AND the
  // event-role's capabilities — a restricted CUSTOM "individual" role must not
  // be offered sections it can't open. Parent entries (guests, check-in,
  // automation, messaging) show when ANY of their sub-pages is permitted; the
  // sub-menu itself is gated per-sub in CoweringDropDown.getNestedItems.
  const dropdownItems = useMemo(() => {
    if (archived) {
      // Deleting is the event OWNER's alone — this row action posts to the
      // same `batch-delete` as the bulk dropdown, which refuses every id the
      // caller does not own (event-scope wave S2d).
      return ["unarchive", ...(event.role === "OWNER" ? ["delete"] : [])];
    }

    const items: string[] = [];
    const plugins = event.plugins || [];
    const caps = resolveEventCaps(
      event.role ?? "",
      event.capabilities as Capability[] | undefined,
    );
    const has = (...c: Capability[]) => c.some((x) => caps.has(x));

    if (plugins.includes(Plugin.DASHBOARD) && has("dashboards:read")) items.push("dashboard");
    if (plugins.includes(Plugin.WEBSITE) && has("website:read")) items.push("website");

    // Guests has no plugin gate. Show the parent only when a sub-page actually
    // exists for this role, matching CoweringDropDown.getNestedItems exactly —
    // otherwise a requests-only role on an OPEN event would get a parent with no
    // subs that navigates straight to the (forbidden) guestlist. requests only
    // exists on LOCKED/INVITATION events.
    const regHasRequests =
      event.registrationType === RegistrationType.LOCKED ||
      event.registrationType === RegistrationType.INVITATION;
    if (
      has("guests:read") ||
      (has("guests:read.companions") && regHasRequests)
    ) {
      items.push("guests/guestlist");
    }

    if (plugins.includes(Plugin.CHECK_IN) && has("check-in-logs:read", "check-in-devices:read")) items.push("check-in");
    // One Programme entry, no "slotting" one: bookable programme items are
    // part of Programme (PROGRAM ⇔ SLOTTING is one switch) and the old entry
    // only redirected here. Gated like its destination — the programme page
    // hard-requires event-program:read (W4/F7: no SNoAccess dead-end) — and
    // on PROGRAM || SLOTTING like the sidebar and the event landing, so a
    // legacy SLOTTING-only row keeps its entry.
    if (
      (plugins.includes(Plugin.PROGRAM) || plugins.includes(Plugin.SLOTTING)) &&
      has("event-program:read")
    ) {
      items.push("programme");
    }
    if (plugins.includes(Plugin.SEATING) && has("seating:read")) items.push("seating");
    if (plugins.includes(Plugin.ATTACHMENTS) && has("attachments:read")) items.push("attachments");
    if (plugins.includes(Plugin.DOCUMENTS) && has("documents:read")) items.push("documents");
    if (plugins.includes(Plugin.ACTIONS) && has("workflows:read")) items.push("automation");
    if (plugins.includes(Plugin.MESSAGING) && has("email-templates:read", "sent-emails:read", "event:settings:write")) items.push("messaging/email-templates");

    // Settings / Duplicate / Archive are OWNER/ADMIN-only operations (the
    // settings sub-pages hard-gate on role, and duplicate/archive are event
    // admin). CoweringDropDown's role filter drops these for everyone else.
    items.push("settings");
    items.push("duplicate");
    items.push("archive");

    return items;
  }, [archived, event.plugins, event.role, event.capabilities, event.registrationType]);

  return (
    <>
      {isEditing ? (
        <></>
      ) : (
        <div className="cursor-default" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          {isLoading && <CoveringLoadingSpinner />}
          <CoweringDropDown
            changeDefault={false}
            border={true}
            initialItem={
              isLoading || isDeleting ? t2("processing") : t2("actions")
            }
            translate={true}
            translation={t}
            allItems={dropdownItems}
            onHoverAction={(item) => {
              if (
                item === "duplicate" ||
                item === "archive" ||
                item === "unarchive" ||
                item === "delete"
              ) {
                return;
              }
              
            }}
            onDropdownAction={handleDropdownAction}
            onNestedAction={handleNestedAction}
            disabled={isDeleting || showDeleteConfirmation || showDuplicateModal}
            event={event} // Pass the event to CoweringDropDown
          />
          <AnimatePresence>
            {showDeleteConfirmation && (
              <PDeleteConfirmation
                onCloseAction={handleCancelDelete}
                onConfirmAction={handleDeleteConfirmed}
                isLoading={isDeleting}
                error={actionError}
                title={t2("title")}
                message={t2("message")}
              >
                {/* PB-EDP-1: a paid event plan ends with the delete, no refund. */}
                {hasPaidEventPlan(event.planType) && (
                  <EventDeletePaidPlanNotice text={tBulk("paidPlanWarningOne")} />
                )}
              </PDeleteConfirmation>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showDuplicateModal && (
              <PDuplicateEvent
                eventName={event.name || ""}
                onCloseAction={handleCancelDuplicate}
                onConfirmAction={handleDuplicateConfirmed}
                isLoading={isDuplicating}
                error={duplicateError}
              />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showDuplicateToast && (
              <PShowToast icon={IconCheck} text={t3("success")} />
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );
};
